import * as React from "react";
import { useIntl } from "react-intl";
import { Form, Message, Icon, Popup, Radio } from "semantic-ui-react";

import { BasicFormProps, Category } from "../../../interfaces";
import { useSocialNetworks, Rules } from "../../../utils";
import { MultipleDaySelectorComponent } from "@/components/MultipleDaySelectorComponent";
import { AddCategoryComponent } from "@/components/AddCategoryComponent";
import { DateRangePicker } from "@/components/DateRangePicker";
import { SocialNetworkIconButton } from "../../SocialNetworkIconButton";

import { CampaignType, descriptors } from "./descriptors";

import "./styles.scss";
import { RowBasicPadding, SocialGrid, TitleLabel } from "../../../styling/baseStyle";

import moment from "moment-timezone";

interface OwnProps extends BasicFormProps {
  /**
   * Handle delivery mode changes
   */
  handleSetFieldValue?: (field: string, value: any) => void;
  basicInfoSubmitRef: any;
}

type Props = OwnProps;

/**
 * Basic Info Step.
 */
const CampaignClass: React.FC<Props> = ({
  handleChange,
  handleSetFieldValue,
  handleSubmit,

  messages,
  values,
  basicInfoSubmitRef,
}) => {
  const { formatMessage } = useIntl();
  const errors = messages && messages.error && messages.error.length > 0;
  const { deliveryMode, socialNetworks } = values;
  const deliveryRandom = deliveryMode === "RANDOM_DELIVERY" || deliveryMode === "ORDERED_RANDOM";
  const [categories, setCategories] = React.useState<Category[]>(values.categories);

  const { postableSocialNetworks } = useSocialNetworks();

  React.useEffect(() => {
    handleSetFieldValue!("categories", categories);
  }, [categories]);

  React.useEffect(() => {
    handleSetFieldValue!("socialNetworksCounter", Object.values(socialNetworks).filter(Boolean).length);
  }, [socialNetworks]);

  /*
   * Auto Post or Push Notification
   */
  const deliveryType = (): JSX.Element => {
    return (
      <SocialGrid.Row className={"xbigPaddingTop mediumPaddingBottom"}>
        <SocialGrid.Column width={10}>
          <RowBasicPadding>
            <TitleLabel>{formatMessage({ ...descriptors[CampaignType.deliveryTypeLabel] })}</TitleLabel>
            <br />
            <label className="descriptionLabel">
              {formatMessage({ ...descriptors[CampaignType.deliveryTypeDescription] })}
            </label>
          </RowBasicPadding>

          <SocialGrid columns={1} className={"bigPaddingLeft"}>
            <SocialGrid.Row key={"campaign_basic_row_auto_post"}>
              <Radio
                data-elm-id={`campaignBasicInfoAutoPostRadioBtn`}
                className={"campaignRadio secondary rounded"}
                id={"isAutoPost"}
                name={"isAutoPost"}
                onChange={(e: any, data: any) => {
                  handleSetFieldValue!(`${data.id}`, data.checked);
                }}
                checked={values.isAutoPost}
                label={"AutoPost"}
              />

              {values.isAutoPost && (
                <>
                  <br />
                  <label className="descriptionLabel xbigPaddingLeft">
                    {formatMessage({ ...descriptors[CampaignType.socialNetworksDescription] })}
                  </label>
                </>
              )}
              {values.isAutoPost && (
                <SocialGrid className={"socialSection"}>
                  <RowBasicPadding className={"socialIconsSection"}>
                    {postableSocialNetworks.map((sn, index) => {
                      return (
                        <SocialNetworkIconButton
                          size={"noMargin"}
                          key={"campaign_sn_icon_" + index}
                          active={socialNetworks[sn]}
                          networkId={sn}
                          onClickHandler={checked => {
                            handleSetFieldValue!("socialNetworks", { ...socialNetworks, [sn]: checked });
                          }}
                        />
                      );
                    })}
                  </RowBasicPadding>
                </SocialGrid>
              )}
            </SocialGrid.Row>

            <SocialGrid.Row key={"campaign_basic_row_push_notification"}>
              <Radio
                data-elm-id={`campaignBasicInfoPushNotificationRadioBtn`}
                className={"campaignRadio secondary rounded"}
                id={"isPushPost"}
                name={"isPushPost"}
                onChange={(e: any, data: any) => handleSetFieldValue!(`isAutoPost`, false)}
                checked={!values.isAutoPost}
                label={"Push Notification"}
              />
            </SocialGrid.Row>
          </SocialGrid>
        </SocialGrid.Column>
      </SocialGrid.Row>
    );
  };

  const customStartEnd = (): JSX.Element => {
    const startDate = values.startDate ? new Date(values.startDate) : values.startDate;
    const endDate = values.endDate ? new Date(values.endDate) : values.endDate;
    return (
      <SocialGrid.Row className={"xbigPaddingTop"}>
        <RowBasicPadding>
          <Popup
            hoverable
            trigger={
              <TitleLabel>
                {formatMessage({ ...descriptors[CampaignType.customDurationLabel] })}
                <Icon name="info circle" className={"infoIcon"} link />
              </TitleLabel>
            }
            content={formatMessage({ ...descriptors[CampaignType.campaignDurationHoverDescription] })}
          />
          <br />
          <label className="descriptionLabel">
            {formatMessage({ ...descriptors[CampaignType.campaignDurationDescription] })}
          </label>
        </RowBasicPadding>
        <DateRangePicker
          startDate={startDate}
          onChangeStartDate={date => handleSetFieldValue!("startDate", date)}
          endDate={endDate}
          onChangeEndDate={date => handleSetFieldValue!("endDate", date)}
        />
      </SocialGrid.Row>
    );
  };

  const deliveryModeSection = (): JSX.Element => {
    const deliveryStatus = [
      {
        key: 0,
        text: formatMessage({ ...descriptors[CampaignType.randomDelivery] }),
        value: "RANDOM_DELIVERY",
        selected: deliveryMode === "RANDOM_DELIVERY",
      },
      {
        key: 1,
        text: formatMessage({ ...descriptors[CampaignType.orderedRandomDelivery] }),
        value: "ORDERED_RANDOM",
        selected: deliveryMode === "ORDERED_RANDOM",
      },
      {
        key: 2,
        text: formatMessage({ ...descriptors[CampaignType.specificDate] }),
        value: "SPECIFIC_DATE",
        selected: deliveryMode === "SPECIFIC_DATE",
      },
    ];

    return (
      <SocialGrid.Row className={"xbigPaddingTop"}>
        <RowBasicPadding>
          <TitleLabel>{formatMessage({ ...descriptors[CampaignType.deliveryLabel] })}</TitleLabel>
          <br />
          <label className="descriptionLabel">
            {formatMessage({ ...descriptors[CampaignType.deliveryModeDescription] })}
          </label>
        </RowBasicPadding>

        <SocialGrid columns={1} className={"bigPaddingLeft mediumPaddingBottom"}>
          {deliveryStatus.map(button => {
            return (
              <React.Fragment key={button.value}>
                <SocialGrid.Row>
                  <Radio
                    data-elm-id={`campaignBasicInfoDeliveryModeBtn`}
                    className={"campaignRadio secondary rounded"}
                    id={button.value}
                    name={button.value}
                    onChange={(e: any, data: any) => {
                      handleSetFieldValue!("deliveryMode", data.name);
                    }}
                    checked={button.selected}
                    label={button.text}
                  />
                </SocialGrid.Row>
                {deliveryRandom && button.value === deliveryMode && (
                  <MultipleDaySelectorComponent
                    description={formatMessage({ ...descriptors[CampaignType.randomDaysDescription] })}
                    selectedDays={values.randomScheduleDays}
                    selectedTime={values.deliveryTimeDatePicker}
                    onChangeDays={days => handleSetFieldValue!("randomScheduleDays", days)}
                    onChangeTime={(date: Date | undefined) => {
                      handleSetFieldValue!("deliveryTimeDatePicker", date);

                      const timePst = moment(date).tz("America/Los_Angeles").format("HH:mm:00");
                      handleSetFieldValue!("randomScheduleDeliveryTimePst", timePst);
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </SocialGrid>
      </SocialGrid.Row>
    );
  };

  const deliverySection = (): JSX.Element => {
    return (
      <SocialGrid.Column width={10}>
        {deliveryType()}
        {deliveryModeSection()}
        {deliveryRandom && customStartEnd()}
      </SocialGrid.Column>
    );
  };

  return (
    <Form
      size="large"
      error={errors}
      ref={basicInfoSubmitRef}
      onSubmit={handleSubmit}
      className={"campaignBasicInfoForm"}
    >
      <div className={"formDiv"}>
        {messages && messages.error && messages.error.length > 0 && <Message error list={messages && messages.error} />}
        <Form.Input
          key={"campaignNameInput"}
          label={formatMessage({ ...descriptors[CampaignType.campaignTitle] })}
          data-elm-id={"campaignNameInput"}
          fluid
          id={"name"}
          type={"text"}
          name={"name"}
          placeholder={formatMessage({ ...descriptors[CampaignType.titlePlaceholder] })}
          onChange={(e, data) => {
            if (e.target.value.length < Rules.MAX_CAMPAIGN_TITLE) {
              handleChange!(e, data);
            }
          }}
          value={values.name}
        />

        <AddCategoryComponent
          customTitleStyle={"titleLabel"}
          selectedCategories={categories}
          handleOnChange={setCategories}
          type={"CAMPAIGN"}
          multiple={false}
        />

        <SocialGrid>{deliverySection()}</SocialGrid>
      </div>
    </Form>
  );
};

export const CampaignBasicInfo = CampaignClass;
